import type { JSX } from "preact";
import { useSignalEffect } from "@preact/signals";
import { composerNotice, mailboxes, mobilePane, view } from "../state";
import { Login } from "./Login";
import { Topbar } from "./Topbar";
import { Sidebar } from "./Sidebar";
import { MessageList } from "./MessageList";
import { Reader, EmlPreviewModal } from "./Reader";
import { Composer } from "./Composer";
import { KeyboardHandler, ShortcutsModal } from "./KeyboardHandler";
import { Settings } from "./Settings";

export function App(): JSX.Element {
  const current = view.value;

  if (current === "loading") return <Splash />;
  if (current === "login") return <Login />;

  return (
    <div class="flex h-full flex-col">
      <Topbar />
      <div class="flex min-h-0 flex-1 overflow-hidden">
        {/* Sidebar: hidden on mobile unless mobilePane==="sidebar" */}
        <div
          class={[
            "flex-col border-r border-subtle h-full",
            mobilePane.value === "sidebar" ? "flex w-full" : "hidden",
            "md:flex md:w-64 md:shrink-0",
          ].join(" ")}
        >
          <Sidebar />
        </div>

        {/* Message list: hidden on mobile unless mobilePane==="list" */}
        <div
          class={[
            "flex-col h-full border-r border-subtle",
            mobilePane.value === "list" ? "flex w-full" : "hidden",
            "md:flex md:w-[400px] md:shrink-0",
          ].join(" ")}
        >
          <MessageList />
        </div>

        {/* Reader: hidden on mobile unless mobilePane==="reader" */}
        <div
          class={[
            "flex-col",
            mobilePane.value === "reader" ? "flex w-full" : "hidden",
            "md:flex md:flex-1 md:min-w-0",
          ].join(" ")}
        >
          <Reader />
        </div>
      </div>
      <Composer />
      <EmlPreviewModal />
      <Toast />
      <KeyboardHandler />
      <ShortcutsModal />
      <Settings />
      <TitleUpdater />
    </div>
  );
}

/** Reactively updates document.title based on inbox unread count. */
function TitleUpdater(): null {
  useSignalEffect(() => {
    const count = mailboxes.value.find((m) => m.specialUse === "inbox")?.unread ?? 0;
    document.title = count > 0 ? `(${count}) MailTrack` : "MailTrack";
  });
  return null;
}

function Toast(): JSX.Element | null {
  const msg = composerNotice.value;
  if (!msg) return null;
  return (
    <div class="fixed bottom-5 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-ink-3 px-4 py-2 text-sm text-fg shadow-lg ring-1 ring-subtle">
      {msg}
    </div>
  );
}

function Splash(): JSX.Element {
  return (
    <div class="flex h-full items-center justify-center bg-ink-0 text-muted">
      <div class="flex items-center gap-3 text-sm">
        <span class="h-5 w-5 animate-spin rounded-full border-2 border-ink-4 border-t-accent" />
        Loading…
      </div>
    </div>
  );
}
